import { FRAME_HEADER_SIZE, decodeFrameHeader, type FrameHeader } from '@relay/shared';

export interface SplitFrame {
  header: FrameHeader;
  payload: Uint8Array;
}

export function splitFrame(buf: ArrayBuffer): SplitFrame {
  if (buf.byteLength < FRAME_HEADER_SIZE) {
    throw new Error(`frame too small: ${buf.byteLength}`);
  }
  const headerBytes = new Uint8Array(buf, 0, FRAME_HEADER_SIZE);
  const payload = new Uint8Array(buf, FRAME_HEADER_SIZE, buf.byteLength - FRAME_HEADER_SIZE);
  const header = decodeFrameHeader(headerBytes);
  return { header, payload };
}

export interface RenderTarget {
  draw: (bitmap: ImageBitmap) => void;
}

export function makeCanvasRenderTarget(canvas: HTMLCanvasElement): RenderTarget {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2d context unavailable');
  return {
    draw(bitmap) {
      if (canvas.width !== bitmap.width) canvas.width = bitmap.width;
      if (canvas.height !== bitmap.height) canvas.height = bitmap.height;
      ctx.drawImage(bitmap, 0, 0);
      bitmap.close();
    },
  };
}

export interface DecodeStats {
  fps: number;
  framesDropped: number;
  framesDecoded: number;
}

export function makeFrameRouter(target: RenderTarget): {
  ingest: (buf: ArrayBuffer) => Promise<void>;
  stats: () => DecodeStats;
  reset: () => void;
} {
  let inFlight: Promise<void> | null = null;
  let lastSeq = -1;
  let framesDropped = 0;
  let framesDecoded = 0;
  let fpsWindow: number[] = [];

  return {
    async ingest(buf) {
      const { header, payload } = splitFrame(buf);
      if (lastSeq >= 0 && header.seq <= lastSeq) {
        framesDropped++;
        return;
      }
      lastSeq = header.seq;
      if (inFlight) {
        framesDropped++;
        return;
      }
      const work = (async () => {
        try {
          const blob = new Blob([payload as BlobPart], { type: 'image/jpeg' });
          const bitmap = await createImageBitmap(blob);
          target.draw(bitmap);
          framesDecoded++;
          const now = performance.now();
          fpsWindow.push(now);
          fpsWindow = fpsWindow.filter((t) => now - t <= 1000);
        } finally {
          inFlight = null;
        }
      })();
      inFlight = work;
      await work;
    },
    stats() {
      return { fps: fpsWindow.length, framesDropped, framesDecoded };
    },
    reset() {
      lastSeq = -1;
      framesDropped = 0;
      framesDecoded = 0;
      fpsWindow = [];
      inFlight = null;
    },
  };
}
